const DEFAULT_DOB = '05/29/2023';

/** Main Functions */
export function getPageInfo() {
  /*
      Description: This function will get the page info from the note
      Return: A dictionary with the page info
          {
              "URL": Base URL for the page,
              "Note Type": "PT Daily/SOAP Note",...
          }
      */
  const baseURL = window.location.origin;

  let noteTypeElement = document.querySelector('h1.note-title');
  let noteType = noteTypeElement ? noteTypeElement.textContent.trim() : null;

  if (noteType) {
    noteType = noteType.replace(/^–\s*/, '').trim(); // Clean up leading dash if present
  }

  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

export function getPatientInfo() {
  let emrID = '';
  let dob = '';
  let firstName = '';
  let lastName = '';

  // 1. Extract Patient Name from the .right-info section
  const nameBlock = document.querySelector('.right-info .ribbon-item-header');
  if (nameBlock?.textContent.trim() === 'Patient Name') {
    const nameElement = nameBlock.nextElementSibling;
    if (nameElement) {
      const rawText = nameElement.textContent.replace(/\s+/g, ' ').trim(); // e.g. "Testtt, Patient"
      const parts = rawText.split(',');
      if (parts.length === 2) {
        lastName = parts[0].trim();
        firstName = parts[1].trim();
      }
    }
  }

  // 2. Loop through .item blocks to get DOB and EMR ID
  const infoBlocks = document.querySelectorAll('.item');
  infoBlocks.forEach((block) => {
    const label = block
      .querySelector('.ribbon-item-header')
      ?.textContent.trim();
    const valueElement = block.querySelector('p');

    if (label === 'Date Of Birth (Age)' && valueElement) {
      const rawDOB = valueElement.childNodes[0]?.textContent.trim();
      if (rawDOB) {
        const dateObj = new Date(rawDOB);
        if (!isNaN(dateObj)) {
          const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
          const dd = String(dateObj.getDate()).padStart(2, '0');
          const yyyy = dateObj.getFullYear();
          dob = `${mm}/${dd}/${yyyy}`;
        }
      }
    }

    if (label === 'Account' && valueElement) {
      emrID = valueElement.textContent.trim();
    }
  });

  return {
    'EMR ID': emrID || '',
    'First Name': firstName || '',
    'Last Name': lastName || '',
    DOB: dob || DEFAULT_DOB
  };
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]

*/
var questions = [];

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Long Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/
var questionInteractions = {};

export async function getQuestions() {
  questions = [];
  questionInteractions = {};

  // Step 1: Ensure "Add Narrative" exists in each section
  await ensureAddNarrativeExists();

  await getFlexQuestions();
  await handleFlexTables();

  //Sort the questions based on Dom order
  // This list must be ordered based on where the OriginalElement (DOM element) is located in the document order
  Object.keys(questionInteractions)
    .sort((a, b) => {
      const elementA = questionInteractions[a].OriginalElement;
      const elementB = questionInteractions[b].OriginalElement;
      if (
        elementA.compareDocumentPosition(elementB) &
        Node.DOCUMENT_POSITION_FOLLOWING
      ) {
        return -1;
      } else {
        return 1;
      }
    })
    .forEach((key) => {
      const question = { ...questionInteractions[key] };
      delete question.OriginalElement;
      if (
        question.Type == 'Flex' &&
        question.Structure &&
        Object.keys(question.Structure).length == 0
      ) {
        return;
      }
      questions.push(question);
    });

  console.log('Sorted questions:', questions);
  questions.forEach((question) => {
    console.log(question.Key);
  });
  return questions;
}

async function ensureAddNarrativeExists() {
  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const getSectionLabel = (headerEl) => {
    return (
      headerEl?.textContent?.split('History')[0].trim() || 'Unknown Section'
    );
  };

  const clickOutsidePopup = () => {
    const backdrop = document.querySelector('body');
    if (backdrop) {
      const evt = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      backdrop.dispatchEvent(evt);
      console.log('🟢 Clicked outside to close popup');
    } else {
      console.warn('⚠️ Could not find backdrop to click');
    }
  };

  // Improved DOM-based check for existing narratives
  const hasExistingNarrative = (section) => {
    const narrativeEl = section.querySelector(
      '.narrative-section, .added-narrative, .narrative-container'
    );
    if (narrativeEl) return true;

    const textMatch = Array.from(section.querySelectorAll('p, span, div')).some(
      (el) => /narrative\s*:|add narrative/i.test(el.textContent || '')
    );

    return textMatch;
  };

  // Improved verify using DOM-based check
  const verifyNarrativeAdded = (section, sectionLabel) => {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        const hasIt = hasExistingNarrative(section);
        if (hasIt) {
          clearInterval(checkInterval);
          resolve(true);
        }
      }, 250);

      setTimeout(() => {
        clearInterval(checkInterval);
        resolve(false);
      }, 3000);
    });
  };

  // Remove duplicate narratives in a section
  const removeDuplicateNarratives = (section, sectionLabel) => {
    const narratives = section.querySelectorAll(
      '.narrative-section, .added-narrative, .narrative-container'
    );
    if (narratives.length > 1) {
      narratives.forEach((el, idx) => {
        if (idx > 0) el.remove();
      });
    }
  };

  const sections = document.querySelectorAll('app-collapsible');
  let narrativesAdded = 0;
  let narrativesSkipped = 0;

  for (const section of sections) {
    const headerEl = section.querySelector('.title');
    const sectionLabel = getSectionLabel(headerEl);

    if (!sectionLabel) {
      console.warn('Unknown section label, skipping');
      continue;
    }

    // More thorough check for existing narratives
    if (hasExistingNarrative(section)) {
      console.log(`Narrative already exists in section: ${sectionLabel}`);
      narrativesSkipped++;
      continue;
    }

    console.log(`Adding narrative to section: ${sectionLabel}`);

    const addLink = section.querySelector('.add-link');
    if (!addLink) {
      console.warn(`No .add-link found in section: ${sectionLabel}`);
      continue;
    }

    // Check if add link is disabled or already processed
    if (addLink.disabled || addLink.style.display === 'none') {
      continue;
    }

    addLink.click();
    await sleep(500); // Increased wait time

    // Wait for popup to appear and find the correct link
    await sleep(500);
    const popupAddLink = document.querySelector(
      '[data-testid="add-narrative-header"]'
    );

    if (popupAddLink) {
      popupAddLink.click();
      console.log(`Narrative link clicked in section: ${sectionLabel}`);
      await sleep(500);

      // Verify the narrative was actually added
      const wasAdded = await verifyNarrativeAdded(section, sectionLabel);
      if (wasAdded) {
        narrativesAdded++;
        removeDuplicateNarratives(section, sectionLabel);
      } else {
        console.warn(`Failed to add narrative to section: ${sectionLabel}`);
      }

      // Try closing the popup
      clickOutsidePopup();
      await sleep(300);
    } else {
      console.warn(
        `Could not find popup 'Add Narrative to this section' for: ${sectionLabel}`
      );
      clickOutsidePopup();
    }

    await sleep(400);
  }
}

function getFlexQuestions() {
  let structure = {};
  const textareas = document.querySelectorAll(
    '[data-testid="text-editor"][contenteditable="true"]'
  );
  const labelCounters = {}; // Tracks duplicate labels per section
  textareas.forEach((textarea) => {
    // Get section, base label, and sub-label
    let sectionLabel = getClosestSectionLabel(textarea);
    let baseLabel = getClosestLabel(textarea);

    // Log warnings if key labels are missing
    if (!baseLabel) {
      console.warn(`Missing label for:`, textarea);
      baseLabel = `Unknown Field (${textarea.id || textarea.name || 'No ID'})`;
    }

    // Generate unique baseLabel with counter if needed
    const labelKey = `${sectionLabel}||${baseLabel}`;
    if (!labelCounters[labelKey]) {
      labelCounters[labelKey] = 1;
    } else {
      labelCounters[labelKey] += 1;
      baseLabel += ` ${labelCounters[labelKey]}`;
    }

    // **Ensure the section exists before adding fields**
    if (!structure[sectionLabel]) {
      structure[sectionLabel] = {
        Question: sectionLabel,
        Structure: {},
        OriginalElement: textarea,
        Type: 'Flex'
      };
    }

    if (!structure[sectionLabel].Structure[baseLabel]) {
      structure[sectionLabel].Structure[baseLabel] = {};
    }

    structure[sectionLabel].Structure[baseLabel][
      `${sectionLabel} - ${baseLabel}`
    ] = {
      'Existing Value': textarea.value || '',
      Input: async (val: string, replyType: string) => {
        if (isEmptyOrNull(val)) {
          return;
        }

        const inputField = textarea; // really a contenteditable div

        if (!inputField || !inputField.isContentEditable) {
          console.error('No contenteditable input field found for', baseLabel);
          return;
        }

        // Focus and click the element
        inputField.focus();
        inputField.click();

        // Modify text based on replyType
        if (replyType === 'append') {
          inputField.textContent = (inputField.textContent + '\n' + val).trim();
        } else {
          inputField.textContent = val;
        }

        // Trigger necessary events for framework/data binding to detect change
        ['input', 'change', 'blur'].forEach((event) =>
          inputField.dispatchEvent(new Event(event, { bubbles: true }))
        );

        console.log(`Updated field: ${baseLabel} with value:`, val);
        // Attach an event listener to update values dynamically
        textarea.addEventListener('input', function () {
          structure[sectionLabel].Structure[baseLabel][
            `${sectionLabel} - ${baseLabel}`
          ]['Existing Value'] = textarea.textContent.trim(); // not .value
        });
      }
    };
  });

  // add the structure to questionInteractions
  Object.keys(structure).forEach((key) => {
    questionInteractions[key] = {
      Type: 'Flex',
      Key: key,
      Question: structure[key]?.Question,
      Structure: structure[key]?.Structure,
      OriginalElement: structure[key]?.OriginalElement
    };
  });
}

function getClosestSectionLabel(editor) {
  let el = editor;

  while (el && el !== document.body) {
    const section = el.querySelector?.('.title');
    if (section && section.id?.startsWith('section-')) {
      // Get only the direct text node (excluding nested elements like .history-link)
      const childNodes = Array.from(section.childNodes);
      const directText = childNodes
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent.trim())
        .join(' ')
        .replace(/\s+/g, ' '); // Clean up whitespace

      return directText;
    }
    el = el.parentElement;
  }
  return '';
}

function getClosestLabel(editor) {
  let current = editor.closest('.sentence-grp');

  if (current) {
    const labelEl = current.querySelector('.sentence-text');
    if (labelEl) {
      return labelEl.textContent.trim();
    }
  }
  return '';
}

function handleFlexTables() {
  const romSentences = document.querySelectorAll('doc-dynamic-sentence');

  romSentences.forEach((sentence, i) => {
    const check = getClosestSectionLabel(sentence); // Dynamically get the label

    if (!questionInteractions[check]) {
      questionInteractions[check] = {
        Type: 'Flex',
        Key: check,
        Question: check,
        Structure: {},
        OriginalElement: sentence
      };
    }

    const existingStructure = questionInteractions[check].Structure;

    const labelEl = sentence.querySelector('.sentence-text');
    const label = labelEl?.textContent.trim() || `Measurement Table ${i + 1}`;

    const table = sentence.querySelector('table.sortTable');
    if (!table) return;

    const headers = Array.from(table.querySelectorAll('thead th')).map((th) =>
      th.textContent.trim()
    );

    const rows = table.querySelectorAll('tbody tr');

    if (!existingStructure[label]) {
      existingStructure[label] = {};
    }

    rows.forEach((row) => {
      const cells = row.querySelectorAll('td');

      const rowLabelCell = Array.from(cells).find(
        (td) =>
          td.textContent &&
          !td.querySelector('input') &&
          td.textContent.trim().length > 0
      );

      const rowLabel = rowLabelCell?.textContent.trim() || 'Unnamed Row';

      for (let i = 0; i < headers.length; i++) {
        const header = headers[i];
        const cell = cells[i];
        const input = cell?.querySelector('input[type="text"]');
        const isWNL = !!cell?.querySelector('.wnl-popover');

        if (!input || header === 'Date') continue;

        const flatKey = `${rowLabel} - ${header}`;

        existingStructure[label][flatKey] = {
          Type: 'String',
          ...(input.value && { 'Existing Value': input.value }),
          ...(isWNL && {
            Description: 'This value must be between -360 and 360 or can be WNL'
          }),
          Input: (val) => {
            if (isEmptyOrNull(val)) {
              return;
            }
            input.value = val;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        };
      }
    });
  });
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

export function answerQuestions(answers) {
  /*
      Description: This function will take the answers and populate the questions
      
      Args: 
          answers: A dictionary of answers
              "Question": "The question in question"
              "Answer": "The answer to the question"
     
      Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
          [
              {
                  Question: "Where was the injury?", 
                  Answer: "Back" 
                  Error: "The error message" (Choice not found, etc)
              },
              ...
           }
  
      */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export async function answerQuestion(question, answer, replyType) {
  /*
      Description: This function will take the answer and populate the question
      
      Args: 
          question: The question in question
          answer: The answer to the question
     
      Return: null if success otherwise a string with the error message
      */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // If it's a flex do so
  if (questionInteractions[question]['Type'] === 'Flex') {
    return await answerFlexQuestion(question, answer, replyType);
  }

  // If it is a Long Answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
        // sleep(1000).then(() => {
        //     questionInteractions[question][answer[i]]()
        // });
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.log('Question: ' + question);
      console.log('Answer:' + answer);
      console.log(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

async function answerFlexQuestion(question, answer, replyType = 'overwrite') {
  try {
    const errors = [];
    const questionObject = questionInteractions[question];

    if (!questionObject) {
      console.error(`Question with Key "${question}" not found.`);
      return {
        Question: question,
        Error: `Question with Key "${question}" not found.`
      };
    }

    console.log('Question found:', questionObject);

    const structure = questionObject.Structure || {};

    /** Do the answering sequentially */
    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      const field = structure[fieldKey];

      if (!field) {
        console.error(`Field "${fieldKey}" not found in the Structure.`);
        errors.push({ Field: fieldKey, Error: 'Field not found in Structure' });
        continue;
      }

      for (const [subFieldKey, value] of Object.entries(fieldDetails)) {
        if (field[subFieldKey]?.Input) {
          try {
            await field[subFieldKey].Input(value, replyType);
          } catch (error) {
            errors.push({
              Field: `${fieldKey}.${subFieldKey}`,
              Error: `Error applying value: ${error.message}`
            });
          }
        } else {
          console.warn(
            `Input function not found for "${fieldKey}.${subFieldKey}"`
          );
          errors.push({
            Field: `${fieldKey}.${subFieldKey}`,
            Error: 'Input function not found'
          });
        }
      }
    }

    /** Parse the errors */
    if (errors.length > 0) {
      console.error('Errors occurred:', errors);
      return errors;
    }

    return null; // Success
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      Question: question,
      Error: `Unexpected error: ${error.message}`
    };
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
